import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { apiEndpoints } from "../../services/apiConfig";

export const dataCountPreview = createAsyncThunk(
  "downloadData/fetchDataCount",
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        return rejectWithValue("No token found. Please login again.");
      }

      const response = await axios.post(
        apiEndpoints.downloadDataCountPreview,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      // Handle different error response structures
      if (error.response?.data?.detail) {
        return rejectWithValue({
          message: error.response.data.detail,
          status: error.response.status,
          data: error.response.data,
        });
      }

      if (error.response?.data?.message) {
        return rejectWithValue({
          message: error.response.data.message,
          status: error.response.status,
          data: error.response.data,
        });
      }

      return rejectWithValue({
        message: error.message || "Failed to update user details",
        status: error.response?.status,
        data: error.response?.data,
      });
    }
  },
);

export const downloadDataCouponCodeValidation = createAsyncThunk(
  "downloadData/downloadDataCouponCodeValidation",
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        return rejectWithValue("No token found. Please login again.");
      }

      const response = await axios.post(
        apiEndpoints.downloadDataCouponCodeValidation,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user details",
      );
    }
  },
);

export const getMyDownloads = createAsyncThunk(
  "downloadData/getMyDownloads",
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        return rejectWithValue("No token found. Please login again.");
      }

      // Build query params for pagination
      const params = new URLSearchParams();
      if (payload?.page) params.append("page", payload.page);
      if (payload?.limit) params.append("limit", payload.limit);

      const queryString = params.toString();
      const url = `${apiEndpoints.getMyDownloads}${queryString ? `?${queryString}` : ""}`;

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch downloads",
      );
    }
  },
);

// New thunk for downloading Excel file
export const downloadExcelFile = createAsyncThunk(
  "downloadData/downloadExcelFile",
  async ({ jobId, fileName }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        return rejectWithValue("No token found. Please login again.");
      }

      const response = await axios.get(
        `${apiEndpoints.downloadExcel}/${jobId}/download-excel`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        },
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName || `download_${jobId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true, message: "Download started successfully" };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to download file",
      );
    }
  },
);

export const retryDownloadContacts = createAsyncThunk(
  "downloadData/retryDownloadContacts",
  async ({ jobId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        return rejectWithValue("No token found. Please login again.");
      }

      const response = await axios.get(
        apiEndpoints.retryDownloadProcessing.replace("{job_id}", jobId),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch subscriptions",
      );
    }
  },
);

export const checkBatchAvailability = createAsyncThunk(
  "downloadData/checkBatchAvailability",
  async ({ sessionId, batchId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        return rejectWithValue("No token found. Please login again.");
      }

      const response = await axios.get(
        apiEndpoints.checkBatchAvailability
          .replace("{session_id}", sessionId)
          .replace("{batch_id}", batchId),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch subscriptions",
      );
    }
  },
);

const downloadDataSlice = createSlice({
  name: "downloadData",
  initialState: {
    // Add these missing states
    loading: false,
    error: null,
    dataCount: 0,
    couponCodeValidationResponse: null,
    myDownloadsResponse: null,
    checkBatchAvailabilityResponse: null,
    myDownloads: {
      items: [],
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
      },
      loading: false,
      error: null,
    },
    downloadLoading: false,
    remainingDownloadLoading: false,
    downloadError: null,
  },

  extraReducers: (builder) => {
    builder
      .addCase(dataCountPreview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(dataCountPreview.fulfilled, (state, action) => {
        state.loading = false;
        state.dataCount = action.payload;
      })
      .addCase(dataCountPreview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(downloadDataCouponCodeValidation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadDataCouponCodeValidation.fulfilled, (state, action) => {
        state.loading = false;
        state.couponCodeValidationResponse = action.payload;
      })
      .addCase(downloadDataCouponCodeValidation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getMyDownloads.pending, (state) => {
        state.myDownloads.loading = true;
        state.myDownloads.error = null;
      })
      .addCase(getMyDownloads.fulfilled, (state, action) => {
        state.myDownloads.loading = false;
        state.myDownloadsResponse = action.payload;
        if (action.payload?.data) {
          state.myDownloads.items = action.payload.data.items || [];
          state.myDownloads.pagination = {
            page: action.payload.data.page || 1,
            limit: action.payload.data.limit || 20,
            total: action.payload.data.total || 0,
          };
        } else if (action.payload?.items) {
          // Handle case where data is directly in the payload
          state.myDownloads.items = action.payload.items || [];
          state.myDownloads.pagination = {
            page: action.payload.page || 1,
            limit: action.payload.limit || 20,
            total: action.payload.total || 0,
          };
        }
      })
      .addCase(getMyDownloads.rejected, (state, action) => {
        state.myDownloads.loading = false;
        state.myDownloads.error = action.payload;
      })
      .addCase(downloadExcelFile.pending, (state) => {
        state.downloadLoading = true;
        state.downloadError = null;
      })
      .addCase(downloadExcelFile.fulfilled, (state) => {
        state.downloadLoading = false;
      })
      .addCase(downloadExcelFile.rejected, (state, action) => {
        state.downloadLoading = false;
        state.downloadError = action.payload;
      })
      .addCase(checkBatchAvailability.pending, (state) => {
        state.remainingDownloadLoading = true;
        state.error = null;
      })
      .addCase(checkBatchAvailability.fulfilled, (state, action) => {
        state.remainingDownloadLoading = false;
        state.checkBatchAvailabilityResponse = action.payload;
      })
      .addCase(checkBatchAvailability.rejected, (state, action) => {
        state.remainingDownloadLoading = false;
        state.error = action.payload;
      });
  },
});

export default downloadDataSlice.reducer;
